import HttpService from './HttpService';

const LIMITS_URL = '/users/limits';
const CHECKOUT_URL = '/subscriptions/checkout';
const PORTAL_URL = '/subscriptions/portal';
const CHECKOUT_STATUS_URL = '/subscriptions/checkout-status';

/**
 * Format error for consistent handling across components.
 * Mirrors the shape used by the other domain services (RoomService, etc.).
 * @param {Error} error - Axios error object
 * @param {string} defaultMessage - Default error message
 * @returns {Object} - { message, code, errorCode, originalError, details }
 *   `code` is the HTTP status; `errorCode` is the backend's machine-readable
 *   `error_code` slug (e.g. "BILLING_UNAVAILABLE" on a 503 when the Stripe SDK
 *   call failed) so callers branch on it instead of string-matching `message`.
 */
const formatError = (error, defaultMessage) => {
  const errorMessage = error.response?.data?.message || error.response?.data?.error || defaultMessage;
  const status = error.response?.status;
  const errorCode = error.response?.data?.error_code;
  const details = error.response?.data?.details || {};

  if (process.env.NODE_ENV !== 'production') {
    console.error(defaultMessage, error);
  }

  return { message: errorMessage, code: status, errorCode, originalError: error, details };
};

const SubscriptionService = {
  /**
   * Fetch the current user's subscription tier, status, and per-category usage.
   * @param {Object} [params] - Optional query params (e.g. { house_id } to scope
   *   the paid-tier room limit to a specific house).
   * @returns {Promise<Object>} {
   *   subscription_tier, subscription_status, subscription_expires_at,
   *   categories: { houses, rooms, items, storage, scans }
   * }
   */
  getLimits: async (params = {}) => {
    try {
      const response = await HttpService.get(LIMITS_URL, { params });
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error fetching subscription limits');
    }
  },

  /**
   * Create a Stripe Checkout Session for upgrading to the paid tier. The backend
   * builds the hosted session and returns its URL; the caller redirects the
   * browser to it (window.location.href). No Stripe.js is required.
   * @param {Object} [opts]
   * @param {string} [opts.successUrl] - Absolute URL Stripe returns to on success
   * @param {string} [opts.cancelUrl] - Absolute URL Stripe returns to on cancel
   * @returns {Promise<string>} The hosted Checkout URL
   */
  createCheckoutSession: async ({ successUrl, cancelUrl } = {}) => {
    try {
      const body = {};
      if (successUrl) body.success_url = successUrl;
      if (cancelUrl) body.cancel_url = cancelUrl;
      const response = await HttpService.post(CHECKOUT_URL, body);
      return response.data.checkout_url;
    } catch (error) {
      throw formatError(error, 'Error starting checkout');
    }
  },

  /**
   * Reconcile a completed Stripe Checkout Session server-side. The success page
   * calls this with the `session_id` Stripe substituted into the success URL;
   * the backend verifies the session belongs to the signed-in user and, when
   * Stripe reports it paid, provisions the paid tier through the same path the
   * webhook uses — so a misconfigured or slow webhook no longer strands the
   * user on "Almost there".
   * @param {string} sessionId - Checkout Session id (`cs_…`) from the URL
   * @returns {Promise<Object>} { status: 'paid'|'pending'|'unpaid', subscription_tier, subscription_status }
   */
  getCheckoutStatus: async (sessionId) => {
    try {
      const response = await HttpService.get(CHECKOUT_STATUS_URL, { params: { session_id: sessionId } });
      return response.data;
    } catch (error) {
      throw formatError(error, 'Error checking checkout status');
    }
  },

  /**
   * Create a Stripe Customer Portal session for self-service billing management
   * (update card, cancel, view invoices). Returns the hosted portal URL to
   * redirect the browser to.
   * @param {Object} [opts]
   * @param {string} [opts.returnUrl] - Absolute URL the portal returns to
   * @returns {Promise<string>} The hosted Customer Portal URL
   */
  openBillingPortal: async ({ returnUrl } = {}) => {
    try {
      const body = {};
      if (returnUrl) body.return_url = returnUrl;
      const response = await HttpService.post(PORTAL_URL, body);
      return response.data.portal_url;
    } catch (error) {
      throw formatError(error, 'Error opening billing portal');
    }
  },
};

export default SubscriptionService;
